import React, { useContext, useEffect, useState } from 'react';

import { withStyles } from '@material-ui/core/styles';
import {
  Avatar,
  IconButton,
  Grid,
  FormControlLabel,
  FormLabel,
  Paper,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  Typography,
} from '@material-ui/core';
import { Archive, Delete, Edit } from '@material-ui/icons';

import moment from 'moment';

import PropTypes from 'prop-types';

import CustomCheckBox from '../../fields/customCheckBox';
import DeleteToolBar from '../../fields/deleteToolBar';
import EndedEnrolmentsFilter from '../../fields/endedEnrolmentsFilter';
import SearchField from '../../fields/searchField';
import TablePaginationActions from '../../fields/tablePaginationActions';
import TimedOutShedulesFilter from '../../fields/timedOutShedulesFilter';

import { filteredSearch, getComparator, getDataSheet, stableSort } from '../../../utils/helpers';

import { complementaryColor, defaultRowsPerPage, defaultSortOrder, rowsPerPageOptions, greenColor, amberColor, redColor } from '../../../utils/constants';

import translations from '../../../languages/i18n';

import fieldStyles from '../../../styles/fieldStyles';
import { customOptionsTableStyles } from '../../../styles/formElementsStyles';

import { SessionContext } from '../../../utils/context';

const BlueSwitch = withStyles({
  switchBase: {
    color: 'default',
    '&$checked': {
      color: complementaryColor,
    },
    '&$checked + $track': {
      backgroundColor: complementaryColor,
    },
  },
  checked: {},
  track: {},
})(Switch);

export default function PatientsList({
  administeredPrograms,
  patientsData, handleViewPatient, handleViewPatientSchedules, handleDelete,
  handleSelectedPatientsData, selectedPatients,
}) {
  const classesField = fieldStyles();
  const classesForm = customOptionsTableStyles();

  const sessionContext = useContext(SessionContext);

  const [filterEnded, setFilterEnded] = useState(false);
  const [filteredUsers, setFilteredUsers] = useState([]); // patientsData.slice());
  const [order, setOrder] = React.useState(defaultSortOrder); // Indica l'ordre per defecte
  const [orderBy, setOrderBy] = React.useState('username'); // Indica la columna per la que ordenam per defecte
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(defaultRowsPerPage);
  const [searchString, setSearchString] = useState('');
  const [showOnlyMine, setShowOnlyMine] = useState(true); // Per defecte només mostram els clínics administats i/o supervisats pel clínic
  const [timedOut, setTimedOut] = useState(false);
  const [timedOutDays, setTimedOutDays] = useState(null);
  const timedOutDaysOptions = [2, 7, 10];

  const filterUserSchedulesStatus = (list, pTimedOut, pTimedOutDays) => {
    const out = [];
    let firtStep = null;
    list.map((item) => {
      if (pTimedOut === true) {
        // Agafam valors caducats
        const prevStep = item.userSchedulesStatus.filter((value) => value < 0);
        // Eliminam aquells que incumpleixen el valor límit caducitat establert
        if (pTimedOutDays !== null) {
          firtStep = prevStep.filter((value) => value >= -pTimedOutDays);
        } else {
          firtStep = [...prevStep];
        }
        // Ordenam - el més crític queda primer [-5,-1] o [1, 3]
        firtStep.sort((a, b) => a - b);
        if (firtStep.length > 0) {
          out.push({ ...item, form: firtStep[0] });
        }
      } else {
        // Agafam valors NO caducats
        firtStep = item.userSchedulesStatus.filter((value) => !(value < 0));
        // Ordenam - el més crític queda primer [-5,-1] o [1, 3]
        firtStep.sort((a, b) => a - b);
        out.push({ ...item, form: firtStep[0] });
      }
    });

    // Si mostram els actius ordenam pacients - el més critic primer
    out.sort((a, b) => a.form - b.form);
    return out;
  };

  const filterEndedEnrolments = (list) => {
    const out = [];
    list.map((item) => {
      // Si trobam algun enrolment amb lastEventIsEnd a false vol dir que no té totes les subscripcions acabades, podem deixar de cercar i s'haurà de mostrar l'item
      const itemIsNotEnded = item.enrolments.some((enrolment) => enrolment.lastEventIsEnd === false);
      // Si no té totes les subscripcions acabades, s'ha de mostrar
      if (itemIsNotEnded) out.push({ ...item});
    });

    out.sort((a, b) => a.form - b.form);
    return out; 

    // TODO: Opció 2 correu Rocio: https://3.basecamp.com/4298697/buckets/30224953/todos/7934921244#__recording_7935057369
    // TODO: Filtrar out per eliminar els pacients que tenguin el camp exitus. Aquest camp conte una data i només estarà a l'objecte si el pacient ha mort.
    // TODO: Només es farà quan es mostren els pacients caducats, convé que quan es mostri el llistat de tots els pacients es puguin veure també els que tenen ENDs
  };

  const onlyMyPatients = () => {
    let patientsIdsAdministeredByClinician = [];
    if (administeredPrograms.length !== 0) { // En cas d'haver entrat com a clínic administrador, obtenim els ids dels pacients dels programes que administra
      const patientsIdsAdministeredByClinicianArray = patientsData.map((patient) => {
        const isAdministered = patient.enrolments.some((enrolment) => administeredPrograms.some((programId) => programId === enrolment.program._id));
        if (isAdministered) return patient._id;
      });
      // Llevam els elements undefined (es corresponen als programes que no administra) de l'array
      patientsIdsAdministeredByClinician = patientsIdsAdministeredByClinicianArray.filter((patient) => patient !== undefined);
    }

    // Tant si s'ha entrat com a clínic administrador o com supervisor, obtenim els ids dels pacients dels enrolments que supervisa
    const patientsIdsSupervisedByClinicianArray = patientsData.map((patient) => {
      const isSupervised = patient.enrolments.some((enrolment) => enrolment.supervisedBy.some((clinician) => clinician.admin === true && clinician.clinician === sessionContext.userId));
      if (isSupervised) return patient._id;
    });
    // Llevam els elements undefined (es corresponen als enrolments que no supervisa) de l'array
    const patientsIdsSupervisedByClinician = patientsIdsSupervisedByClinicianArray.filter((patient) => patient !== undefined);

    // Unim els ids dels pacients administrats amb els supervisats i eliminam els duplicats
    const patientsIdsAdministeredAndSupervisedWithDuplicates = patientsIdsAdministeredByClinician.concat(patientsIdsSupervisedByClinician);
    const patientsIdsAdministeredAndSupervised = [...new Set(patientsIdsAdministeredAndSupervisedWithDuplicates)];

    // De tots els pacients, ens quedam amb els que tenen un id administat o supervisat
    const filteredPatients = patientsData.filter((patient) => patientsIdsAdministeredAndSupervised.some((patientId) => patientId === patient._id));
    return filteredPatients;
  };

  // Per a que faci l'await i esperi a acabar l'execució onlyMyPatients() ho he de fer així:
  useEffect(() => {
    const initialOnlyMyPatients = async () => {
      // per defecte no veim els caducats
      const filteredPatientsData = filterUserSchedulesStatus(showOnlyMine ? await onlyMyPatients() : patientsData, timedOut, timedOutDays);

      const data = filteredSearch(filteredPatientsData, searchString, ['username', 'enrolments']);
      if (filterEnded) {
        // Es filtren els que compleixen la condició d'end
        const newPatientsData = filterEndedEnrolments(data);
        setFilteredUsers(newPatientsData);
      } else {
        // Si entram perquè s'ha passat filterEnd a false carregam les dades sense aplicar el filtre de END      
        setFilteredUsers(data);
      }
    };
    // Per defecte, inicialment només mostram els clinics administrats i/o supervisats pel clinic
    initialOnlyMyPatients();
  }, [administeredPrograms, patientsData, showOnlyMine]);


  // Comprovam si el clínic que fa la consulta és administrador del programa al que està subscrit el pacient
  const administersProgram = (programId) => {
    return administeredPrograms.find((program) => program === programId);
  };

  const handleCheck = (id, username) => {
    handleSelectedPatientsData(id, username);
  };

  // const emptyRows = rowsPerPage - Math.min(rowsPerPage, rows.length - page * rowsPerPage);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const patientProgramsList = (enrolmentsArray) => {
    return (
      <React.Fragment>
        {enrolmentsArray.map((enrolment, index) => (
          <Typography key={`patProLis_${index}`} variant='body2'>            
            {(administersProgram(enrolment.program._id) && (translations[enrolment.program.sharedId] ? translations[enrolment.program.sharedId].toUpperCase() : enrolment.program.name.toUpperCase())) || (translations[enrolment.program.sharedId] || enrolment.program.name)}
            <br />
          </Typography>
        ))}
      </React.Fragment>
    );
  };

  const searchPatient = (string) => {
    setPage(0);
    setSearchString(string);
    let data = [];
    if (showOnlyMine) {
      data = filteredSearch(onlyMyPatients(), string, ['username', 'enrolments']);
    } else {
      data = filteredSearch(patientsData, string, ['username', 'enrolments']);
    }
    const newPatientsData = filterUserSchedulesStatus(data, timedOut, timedOutDays);
    setFilteredUsers(newPatientsData);
  };

  useEffect(() => {
    let data = [];
    if (showOnlyMine) {
      data = filteredSearch(onlyMyPatients(), searchString, ['username', 'enrolments']);
    } else {
      data = filteredSearch(patientsData, searchString, ['username', 'enrolments']);
    }
    const newPatientsData = filterUserSchedulesStatus(data, timedOut, timedOutDays);
    setFilteredUsers(newPatientsData);
    if (!timedOut) { // Si no es mostren els caducats, no es pot filtrar per END
      setFilterEnded(false);
    }
  }, [timedOut, timedOutDays]);

  useEffect(() => {
    let data = [];
    // Es treballa damunt el llistat de pacients que es mostra per pantalla, per tant es comprova showOnlyMine i es té en compte la caducitat
    const filteredPatientsData = filterUserSchedulesStatus(showOnlyMine ? onlyMyPatients() : patientsData, timedOut, timedOutDays);
    // Li aplicam el filtre de cerca, per si l'usuari ha introduït algun text
    data = filteredSearch(filteredPatientsData, searchString, ['username', 'enrolments']);
    
    if (filterEnded) {
      // Es filtren els que compleixen la condició d'end
      const newPatientsData = filterEndedEnrolments(data);
      setFilteredUsers(newPatientsData);
    } else {
      // Si entram perquè s'ha passat filterEnd a false carregam les dades sense aplicar el filtre de END      
      setFilteredUsers(data);
    }
  }, [filterEnded]);

  const patientsDataColumns = 4; // Per saber el nombre de propietats de cada objecte

  return (
    <TableContainer component={Paper}>
      <Table aria-label='patients list table'>
        <TableHead>
          <TableRow>
            {/* ATENCIÓ: si pintam la columna _id afegir +1 al colSpan */}
            <TableCell colSpan={patientsDataColumns + 2} align='left' padding='none'>
              {(selectedPatients.length > 0 ? (
                <DeleteToolBar numSelected={selectedPatients.length} handleClick={handleDelete} />
              ) : (
                <Grid container direction='row' alignItems='center' justify='flex-start' spacing={4}>
                  <Grid item>
                    <SearchField handleChange={(string) => searchPatient(string)} value={searchString} />
                  </Grid>
                  <Grid item>
                    <TimedOutShedulesFilter
                      timedOut={timedOut}
                      setTimedOut={setTimedOut}
                      days={timedOutDays}
                      setDays={setTimedOutDays}
                      daysOptions={timedOutDaysOptions}
                    />
                  </Grid>
                  <Grid item>
                    <EndedEnrolmentsFilter
                      filterEnded={filterEnded}
                      setFilterEnded={setFilterEnded}
                      disabled={!timedOut} // El filtre només està habilitat si es mostren els pacients caducats
                    />
                  </Grid>
                  <Grid item>
                    <Grid item>
                      <FormLabel className={classesField.formLabel}>{translations['_onlyMine']}</FormLabel>
                      <FormControlLabel
                        control={(
                          <BlueSwitch
                            checked={!showOnlyMine}
                            onChange={(event) => { setShowOnlyMine(!event.target.checked); }}
                            name='showOnlyMine'
                          />
                        )}
                        label={translations['_everyone']}
                        className={classesField.formControlLabel}
                      />
                    </Grid>
                  </Grid>
                  <Grid item>
                    <IconButton size='medium' onClick={() => getDataSheet(filteredUsers, ['username', 'enrolments', 'sex', 'birthdate'])}>
                      <Archive />
                    </IconButton>
                  </Grid>
                </Grid>
              ))}
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          <TableRow className={classesForm.tableRowHeaderBgColor}>
            {/* ATENCIÓ: no pintam la columna _id */}
            <TableCell align='left'>
              <TableSortLabel
                active={orderBy === 'username'}
                direction={orderBy === 'username' ? order : 'asc'}
                onClick={() => handleRequestSort('username')}
              >
                <strong>{translations['_username']}</strong>
              </TableSortLabel>
            </TableCell>
            <TableCell align='left'><strong>{translations['_programs']}</strong></TableCell>
            {/* <TableCell align='left'>
              <TableSortLabel
                active={orderBy === 'email'}
                direction={orderBy === 'email' ? order : 'asc'}
                onClick={() => handleRequestSort('email')}
              >
                <strong>{translations['_email']}</strong>
              </TableSortLabel>
            </TableCell> */}
            <TableCell align='left'><strong>{translations['_birthdate']}</strong></TableCell>
            <TableCell align='left'><strong>{translations['_sex']}</strong></TableCell>
            <TableCell align='left'><strong>{translations['_forms']}</strong></TableCell>{/* nova columna */}
            <TableCell align='center' />{/* Columna per al botó d'editar */}
            {/* <TableCell align='center'>{sessionContext.role === 'Admin' && <Delete className={classesField.iconBody} />}</TableCell> */}
            <TableCell align='center'><Delete className={classesField.iconBody} /></TableCell>
          </TableRow>
          {(rowsPerPage > 0
            // ATENCIÓ: l'ordenació es fa damunt filteredSchedules, si no s'ha introduit res al camp de cerca el filteredSchedules inclou tot el schedulesData,
            // és a dir, l'ordenació no es fa sobre la "pagina de formularis" que es mostra al llistat (estigui filtrada o no)
            ? stableSort(filteredUsers, getComparator(order, orderBy)).slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
            : stableSort(filteredUsers, getComparator(order, orderBy))
          ).map((data, index) => (
              <TableRow key={data._id} className={index % 2 ? classesForm.tableRowBgColor1 : classesForm.tableRowBgColor2} hover>
                {/* ATENCIÓ: no pintam la columna _id */}
                {/* Només un Admin pot editar/veure la info d'un clínic */}
                <TableCell align='left'>{data.username}</TableCell>
                <TableCell align='left'>{data.enrolments !== null ? patientProgramsList(data.enrolments) : translations['_infoNotAvailable']}</TableCell>
                {/* <TableCell align='left'>{data.email}</TableCell> */}
                <TableCell align='left'>
                  {data.birthdate && moment(data.birthdate).format('DD-MM-YYYY')}
                </TableCell>
                <TableCell align='left'>
                  {data.sex && (data.sex === 'M' ? translations['_man'] : data.sex === 'W' ? translations['_woman'] : translations['_otherSex'])}                  
                </TableCell>
                <TableCell align='left'>
                  {data.form !== null && data.form !== undefined
                    && (
                      <IconButton onClick={() => handleViewPatientSchedules(data._id)}>
                        <Avatar
                          style={
                            data.form > 0
                            ? (data.form > 2 ? { color: '#fff', backgroundColor: greenColor } : { color: '#fff', backgroundColor: amberColor })
                            : { color: '#fff', backgroundColor: redColor }
                          }
                        >
                          { data.form }
                        </Avatar>
                      </IconButton>
                    )}
                </TableCell>
                <TableCell align='center'>
                  <IconButton onClick={() => handleViewPatient(data._id)}>
                    <Edit />
                  </IconButton>
                </TableCell>
                <TableCell align='center'>
                  <CustomCheckBox
                    checked={selectedPatients.findIndex((item) => item.id === data._id) > -1} // Si res.value es dins la taula la condició dona true
                    onClick={() => handleCheck(data._id, data.username)}
                    // disabled={sessionContext.role !== 'Admin'} // Només l'administrador pot eliminar pacietns
                    disabled={true} // Fins que no estigui programat al servidor la correcta eliminació de pacients, ho mantindrem deshabilitat
                  />
                </TableCell>
              </TableRow>
          ))}
        </TableBody>
        <TableFooter>
          <TableRow>
            <TablePagination
              colSpan={patientsDataColumns + 2}
              count={filteredUsers.length}
              rowsPerPageOptions={rowsPerPageOptions}
              rowsPerPage={rowsPerPage}
              page={page}
              SelectProps={{
                inputProps: { 'aria-label': 'rows per page' },
                native: true,
              }}
              labelDisplayedRows={({ from, to, count }) => (
                from + ' - ' + ((to !== -1 && to) || count) + ' ' + translations['_of'] + ' ' + ((count !== -1 && count) || (translations['_moreThan'] + ' ') + to)
              )}
              labelRowsPerPage={translations['_rowsPerPage']}
              onChangePage={handleChangePage}
              onChangeRowsPerPage={handleChangeRowsPerPage}
              ActionsComponent={TablePaginationActions}
            />
          </TableRow>
        </TableFooter>
      </Table>
    </TableContainer>
  );
}

// Indica i controla els props que se han de passar al component
PatientsList.propTypes = {
  administeredPrograms: PropTypes.array.isRequired,
  handleViewPatient: PropTypes.func.isRequired,
  handleViewPatientSchedules: PropTypes.func.isRequired,
  handleDelete: PropTypes.func.isRequired,
  handleSelectedPatientsData: PropTypes.func.isRequired,
  patientsData: PropTypes.array.isRequired,
  selectedPatients: PropTypes.array.isRequired,
};
